import { MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardText, MDBCardTitle, MDBCol, MDBIcon, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalHeader, MDBModalTitle, MDBRow, MDBTable, MDBTableBody, MDBTableHead, MDBFile, MDBSpinner, MDBAlert, MDBTooltip } from "mdb-react-ui-kit";
import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { isoToGermanDate } from "../../../utils/dateTools";
import { getUrl } from "aws-amplify/storage";
import ReactCountryFlag from "react-country-flag";
import ReactPlayer from 'react-player';
import { useIndustry } from "../../../utils/industryContextHook";
import { UserContext, DevContext } from "../../../App";
import { captureError } from "../../../utils/sentryHelpers";
// Enhanced imports using new hooks and validation
import { useLecturesManager } from "../../../components-user/event-management/lecture-registration/hooks/useLecturesManager";
import { useEventsManager } from "../../../components-user/event-management/lecture-registration/hooks/useEventsManager";
import { useCompanyData } from "../../../components-user/event-management/lecture-registration/hooks/useCompanyData";
import { useLectureFileManager } from "../../../components-user/event-management/lecture-registration/hooks/useLectureFileManager";
import LectureRegistration from "../../../components-user/event-management/lecture-registration/Index";
// Import event configuration
import lounges26LectureConfig from "./lectureEventConfig";

export default function AllLecturesEnhanced({adminActions}) {
    const { t, i18n } = useTranslation();
    const { currentIndustry } = useIndustry();
    const { user } = useContext(UserContext);
    const { isDevMode } = useContext(DevContext);
    
    // Check if the current user has the CompanyAdmin group
    const isCompanyAdmin = user?.["cognito:groups"]?.includes('CompanyAdmin');
    
    const companyid = window.location.pathname.includes("mycompany")
    ? window.location.pathname.split("/")[2]
    : window.location.pathname.split("/")[3];

    // Enhanced hooks for data management
    const {
        lectures,
        validationErrors,
        isLoading: isLoadingLectures,
        isDeletingLecture,
        error: lecturesError,
        deleteLecture,
        addLecture,
        updateLecture,
        getLectureValidationErrors,
        isLectureValid,
        getLecturesByStatus,
        canEditLecture,
        canDeleteLecture,
        refreshLectures
    } = useLecturesManager(companyid, currentIndustry?.id, user);

    const {
        isLoading: isLoadingEvents,
        error: eventsError,
        isRegistrationOpen,
        isRegistrationUpcoming,
        canUploadDocuments: eventCanUploadDocuments,
        canUploadPresentations: eventCanUploadPresentations,
        canEditLectures: eventCanEditLectures,
        getEventsByRegistrationStatus
    } = useEventsManager(companyid, currentIndustry?.id);

    const { employees } = useCompanyData(companyid);

    const {
        isUploading: isUploadingLectureFiles,
        uploadErrors: uploadingLecturesErrors,
        uploadFile,
        downloadFile,
        removeFile,
        clearUploadError
    } = useLectureFileManager();

    // DevMode-aware permission checks
    const canEditLectureDevMode = (lecture) => {
        // Im DevMode sind Edit-Buttons immer verfügbar
        if (isDevMode) {
            return true;
        }
        return canEditLecture(lecture);
    };

    const canDeleteLectureDevMode = (lecture) => {
        // Im DevMode sind Delete-Buttons immer verfügbar
        if (isDevMode) {
            return true;
        }
        return canDeleteLecture(lecture);
    };

    // Helper function to check if an event is old (endDate > 14 days ago)
    const isOldEvent = (event) => {
        if (!event?.endDate) return false;
        const endDate = new Date(event.endDate);
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        return endDate < fourteenDaysAgo;
    };

    // Separate lectures into current and old event lectures
    const currentEventLectures = lectures.filter(lecture => !isOldEvent(lecture.event));
    const oldEventLectures = lectures.filter(lecture => isOldEvent(lecture.event));

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEventForLecture, setSelectedEventForLecture] = useState(null);
    const [selectedLectureForEdit, setSelectedLectureForEdit] = useState(null);
    const [deleteLectureModal, setDeleteLectureModal] = useState(false);
    const [selectedLectureRemove, setSelectedLectureRemove] = useState(null);

    // Video modal states
    const [videoModal, setVideoModal] = useState(false);
    const [selectedLectureVideo, setSelectedLectureVideo] = useState(null);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const [statisticsModal, setStatisticsModal] = useState(false);

    // Document upload modal states
    const [documentUploadModal, setDocumentUploadModal] = useState(false);
    const [presentationUploadModal, setPresentationUploadModal] = useState(false);
    const [selectedLectureForDocuments, setSelectedLectureForDocuments] = useState(null);


    // Check if company has reached the lecture limit for an event
    const hasReachedLectureLimit = (event) => {
        if (!event) return false;
        
        // ========================================
        // TEST HARDCODED RULE - START
        // ========================================
        // Allow specific companies to have higher lecture limits
        const ISPE_COMPANY_ID = "1bcac3aa-44b5-4ca3-a736-eb8dc14692eb"; // ISPE: 20 lectures
        const SKAN_COMPANY_ID = "4509771c-c09a-4de0-a94d-9259385d4a1d"; // SKAN: 6 lectures
        const KLIMAB_ID = "4b14c288-5f8b-4484-9e54-a62a18e45982"; // KLIMAAB: 6 lectures
        const PTS_ID = "6bee1c5a-aa84-4ffe-8bd7-485cf245a9b6"; // PTS: 6 lectures
        const DITTEL_ID = "f04fd243-69ca-445a-b0a7-328e8f88a155";
        const CLS_ID = "3ea602b2-8484-4f51-be67-4c96df9e8282";
        const DRES_ID = "75700e5f-f361-437c-9fd2-6b1a3ab1fbe3";

        let lectureLimit = 3; // Default limit
        if (companyid === ISPE_COMPANY_ID) {
            lectureLimit = 20;
        } else if (companyid === SKAN_COMPANY_ID) {
            lectureLimit = 10;
        } else if (companyid === KLIMAB_ID) {
            lectureLimit = 4   ; 
        } else if (companyid === PTS_ID) {
            lectureLimit = 6   ; 
        } else if (companyid === DITTEL_ID) {
            lectureLimit = 4;
        } else if (companyid === CLS_ID) {
            lectureLimit = 4;
        } else if (companyid === DRES_ID) {
            lectureLimit = 4;
        }

        // ========================================
        // TEST HARDCODED RULE - END
        // ========================================
        
        // Count lectures for this specific event that match the filtered types
        // This regex matches the same pattern used in useLecturesManager.js line 45
        const eventLectures = lectures.filter(lecture => 
            lecture.event?.id === event.id &&
            lecture.type?.match(/(marketing|technical|educational|vip|marketing_wien|educational_wien|technical_wien|unset_lecture)/)
        );
        
        return eventLectures.length >= lectureLimit;
    };

    // Enhanced modal handlers
    const handleOpenCreateModal = (event) => {
        if (hasReachedLectureLimit(event)) {
            return; // Don't open modal if limit is reached
        }
        setSelectedEventForLecture(event);
        setShowCreateModal(true);
    };
    
    const handleCloseModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedEventForLecture(null);
        setSelectedLectureForEdit(null);
    };
    
    const handleSaveLecture = (lectureData) => {
        console.log('Lecture saved:', lectureData);

        // Enrich lecture data with event relation if missing
        const enrichedLectureData = {
            ...lectureData,
            event: lectureData.event || selectedEventForLecture
        };

        // Update local state based on whether it's new or edited
        // The API now returns complete data including all relations (speakers, topics, etc.)
        if (selectedLectureForEdit) {
            updateLecture(enrichedLectureData);
        } else {
            addLecture(enrichedLectureData);
        }

        // Close modal
        handleCloseModals();
    };
    
    const handleEditLecture = (lecture) => {
        setSelectedLectureForEdit(lecture.id);
        setSelectedEventForLecture(lecture.event);
        setShowEditModal(true);
    };

    // Enhanced delete handler
    const handleDeleteLecture = async (lectureId) => {
        try {
            await deleteLecture(lectureId);
            setSelectedLectureRemove(null);
            setDeleteLectureModal(false);
        } catch (error) {
            console.error("Error deleting lecture:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'delete-lecture',
                consoleMessage: 'Error deleting lecture:',
                context: {
                    lectureId,
                    companyId: companyid
                }
            });
            // You might want to show a user-friendly error message here
        }
    };

    // Video handling
    const handleOpenVideoModal = async (lectureId) => {
        setSelectedLectureVideo(lectureId);
        const lecture = lectures.find(l => l.id === lectureId);
        const videoKey = lecture?.videoS3Key?.s3Path;
        if (videoKey) {
            const videoUrl = await getVideoUrl(videoKey);
            setSelectedVideoUrl(videoUrl);
        }
        setVideoModal(true);
    };

    const getVideoUrl = async (videoKey) => {
        try {
            const result = await getUrl({
                key: videoKey,
                level: 'public'
            });
            return result.url.href;
        } catch (error) {
            console.log("Error getting video url:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'get-video-url',
                consoleMessage: 'Error getting video url:',
                context: {
                    videoKey
                }
            });
        }
    };

    const handleOpenStatisticsModal = (lectureId) => {
        setSelectedLectureVideo(lectureId);
        setStatisticsModal(true);
    };

    // Document upload handlers
    const handleOpenDocumentUploadModal = (lectureId) => {
        setSelectedLectureForDocuments(lectureId);
        setDocumentUploadModal(true);
    };

    const handleOpenPresentationUploadModal = (lectureId) => {
        setSelectedLectureForDocuments(lectureId);
        setPresentationUploadModal(true);
    };

    const handleSetFile = async (file, fileType) => {
        if (!file || !selectedLectureForDocuments) return;
        
        try {
            const result = await uploadFile(file, fileType, selectedLectureForDocuments);
            
            // Update the lecture using the hook
            const lectureToUpdate = lectures.find(l => l.id === selectedLectureForDocuments);
            if (lectureToUpdate) {
                const updated = {
                    ...lectureToUpdate,
                    ...(fileType === 'De' || fileType === 'En' 
                        ? { [`pdf${fileType}`]: result.fileInfo }
                        : { presentation: result.fileInfo })
                };
                updateLecture(updated);
            }
            
        } catch (error) {
            console.log("Error uploading file:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'upload-lecture-file',
                consoleMessage: 'Error uploading file:',
                context: {
                    fileType,
                    lectureId: selectedLectureForDocuments
                }
            });
            // Error is already handled by the hook
        }
    };

    const handleDownloadFile = async (fileName, path) => {
        try {
            await downloadFile(fileName, path);
        } catch (error) {
            console.log("Error downloading file:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'download-lecture-file',
                consoleMessage: 'Error downloading file:',
                context: {
                    fileName,
                    path
                }
            });
        }
    };

    const handleRemoveFile = async (fileName, path, fileType, lectureId) => {
        try {
            await removeFile(fileName, fileType, lectureId);
            
            // Update local state
            const lectureToUpdate = lectures.find(l => l.id === lectureId);
            if (lectureToUpdate) {
                const updated = {
                    ...lectureToUpdate,
                    ...(fileType === 'De' ? { pdfDe: null } : {}),
                    ...(fileType === 'En' ? { pdfEn: null } : {}),
                    ...(fileType === 'Presentation' ? { presentation: null } : {})
                };
                updateLecture(updated);
            }
            
        } catch (error) {
            console.log("Error removing file:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'remove-lecture-file',
                consoleMessage: 'Error removing file:',
                context: {
                    fileName,
                    path,
                    fileType,
                    lectureId
                }
            });
        }
    };

    // Enhanced permission checks using event data
    const canUploadDocumentsForLecture = (lecture) => {
        if (isDevMode) {
            return isCompanyAdmin;
        }

        // Get the user's Cognito sub (user ID)
        const userSub = user?.sub || user?.username;

        // Check if user is in the usersCanEdit array
        const isUserInEditList = lecture.usersCanEdit && Array.isArray(lecture.usersCanEdit)
            ? lecture.usersCanEdit.includes(userSub)
            : false;

        // User can upload if they are CompanyAdmin OR if they are in the usersCanEdit array
        const hasPermission = isCompanyAdmin || isUserInEditList;

        return hasPermission &&
               lecture.event &&
               eventCanUploadDocuments(lecture.event);
    };

    const canUploadPresentationForLecture = (lecture) => {
        if (isDevMode) {
            return isCompanyAdmin;
        }

        // Get the user's Cognito sub (user ID)
        const userSub = user?.sub || user?.username;

        // Check if user is in the usersCanEdit array
        const isUserInEditList = lecture.usersCanEdit && Array.isArray(lecture.usersCanEdit)
            ? lecture.usersCanEdit.includes(userSub)
            : false;

        // User can upload if they are CompanyAdmin OR if they are in the usersCanEdit array
        const hasPermission = isCompanyAdmin || isUserInEditList;

        return hasPermission &&
               lecture.event &&
               eventCanUploadPresentations(lecture.event);
    };

    // Show loading state
    if (isLoadingLectures || isLoadingEvents) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <MDBSpinner style={{ width: '3rem', height: '3rem' }} className="me-3">
                    <span className="visually-hidden">{t("Loading")}</span>
                </MDBSpinner>
                <span>{t('Loading lectures and events')}</span>
            </div>
        );
    }

    // Show error state
    if (lecturesError || eventsError) {
        return (
            <MDBAlert color="danger" className="mb-4">
                <h4 className="alert-heading">{t('Error Loading Data')}</h4>
                <p>{lecturesError || eventsError}</p>
                <hr />
                <p className="mb-0">
                    <MDBBtn color="danger" outline onClick={() => {
                        refreshLectures();
                    }}>
                        {t('Try Again')}
                    </MDBBtn>
                </p>
            </MDBAlert>
        );
    }

    return (
        <>
            {/* Enhanced Events Section */}
            <MDBRow className="mb-3 mb-lg-5">
                {getEventsByRegistrationStatus('active').map((event) => (
                    <MDBCol size="12" lg="6" key={event.id}>
                        <MDBCard className="mx-auto text-center h-100" style={isRegistrationUpcoming(event) ? {backgroundColor: "#f8f9fa"} : {}}>
                            <MDBCardBody className="d-flex flex-column">
                                <MDBCardTitle>
                                    <h4>{event.name?.find(n => n.language === i18n.language)?.text ?? event.name?.[0]?.text}</h4>
                                </MDBCardTitle>
                                
                                {isRegistrationOpen(event) && (
                                    <h6 className="text-success mb-3">
                                        <MDBIcon fas icon="check-circle" className="me-2" />
                                        {t("Submission Open")}
                                    </h6>
                                )}
                                
                                {isRegistrationUpcoming(event) && (
                                    <div className="mb-3">
                                        <h6 className="text-muted">
                                            <MDBIcon fas icon="clock" className="me-2" />
                                            {t("Submission opens on")}
                                        </h6>
                                        <p className="fw-bold">{isoToGermanDate(event.lectureRegistrationStartDate)}</p>
                                    </div>
                                )}
                                
                                <div className="mb-3 small text-muted">
                                    {event.lectureRegistrationEndDate && (
                                        <div><strong>{t("Submission Deadline")}:</strong> {isoToGermanDate(event.lectureRegistrationEndDate)}</div>
                                    )}
                                    <div><strong>{t("Event Date")}:</strong> {isoToGermanDate(event.startDate)} - {isoToGermanDate(event.endDate)}</div>
                                </div>
                                
                                <div className="mt-auto">
                                    {/* Im DevMode sind Register-Buttons immer verfügbar */}
                                    {isDevMode ? (
                                        <div>
                                            <div className="d-flex gap-2 justify-content-center mb-2">
                                                {hasReachedLectureLimit(event) ? (
                                                    <MDBBtn color="secondary" disabled>
                                                        <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                                                        {t("Lecture Limit Reached")}
                                                    </MDBBtn>
                                                ) : (
                                                    <MDBBtn color="primary" onClick={() => handleOpenCreateModal(event)}>
                                                        <MDBIcon fas icon="plus" className="me-2" />
                                                        {t("Register Lecture")}
                                                    </MDBBtn>
                                                )}
                                               
                                            </div>
                                            {hasReachedLectureLimit(event) ? (
                                                <div className="text-center">
                                                    <small className="text-warning d-block mb-2">
                                                        <MDBIcon fas icon="info-circle" className="me-1" />
                                                        {/* TEST HARDCODED RULE: Show dynamic limit based on company */}
                                                        {t(`You have reached the maximum limit of ${
                                                            companyid === "1bcac3aa-44b5-4ca3-a736-eb8dc14692eb" ? 20 :
                                                            companyid === "4509771c-c09a-4de0-a94d-9259385d4a1d" ? 6 : 3
                                                        } lectures for this event.`)}
                                                    </small>
                                                    <small className="text-muted d-block">
                                                        {t("If you need to increase this limit, please contact us.")}
                                                    </small>
                                                </div>
                                            ) : (
                                                <small className="text-info d-block text-center">
                                                    <MDBIcon fas icon="code" className="me-1" />
                                                    DevMode: Always enabled
                                                </small>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            {isRegistrationOpen(event) ? (
                                                <div>
                                                    <div className="d-flex gap-2 justify-content-center mb-2">
                                                        {hasReachedLectureLimit(event) ? (
                                                            <MDBBtn color="secondary" disabled>
                                                                <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                                                                {t("Lecture Limit Reached")}
                                                            </MDBBtn>
                                                        ) : (
                                                            <MDBBtn color="primary" onClick={() => handleOpenCreateModal(event)}>
                                                                <MDBIcon fas icon="plus" className="me-2" />
                                                                {t("Register Lecture")}
                                                            </MDBBtn>
                                                        )}
                                                    </div>
                                                    {hasReachedLectureLimit(event) && (
                                                        <div className="text-center">
                                                            <small className="text-warning d-block mb-2">
                                                                <MDBIcon fas icon="info-circle" className="me-1" />
                                                                {/* TEST HARDCODED RULE: Show dynamic limit based on company */}
                                                                {t(`You have reached the maximum limit of ${
                                                                    companyid === "1bcac3aa-44b5-4ca3-a736-eb8dc14692eb" ? 20 :
                                                                    companyid === "4509771c-c09a-4de0-a94d-9259385d4a1d" ? 6 : 3
                                                                } lectures for this event.`)}
                                                            </small>
                                                            <small className="text-muted d-block">
                                                                {t("If you need to increase this limit, please contact us.")}
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <MDBBtn color="secondary" disabled>
                                                    {isRegistrationUpcoming(event)
                                                        ? t("Submission Not Yet Open")
                                                        : t("Submission Closed")
                                                    }
                                                </MDBBtn>
                                            )}
                                        </>
                                    )}
                                </div>
                            </MDBCardBody>
                        </MDBCard>
                    </MDBCol>
                ))}
                
                {getEventsByRegistrationStatus('active').length === 0 && (
                    <MDBCol size="12">
                        <MDBAlert color="info" className="text-center">
                            <h5>{t("No Events Available")}</h5>
                            <p>{t("There are currently no events available for lecture registration. Please check back later or contact support if you believe this is an error.")}</p>
                        </MDBAlert>
                    </MDBCol>
                )}
            </MDBRow>

            {/* Dev Mode: Overall Validation Summary */}
            {isDevMode && (
                <MDBCard className="mb-4 border-warning">
                    <MDBCardBody className="bg-light">
                        <MDBCardTitle className="text-warning">
                            <MDBIcon fas icon="code" className="me-2" />
                            Dev Mode: Validation Summary
                        </MDBCardTitle>
                        <div className="row">
                            <div className="col-md-6">
                                <h6>Overall Validation Errors:</h6>
                                <pre className="small" style={{fontSize: '11px', maxHeight: '200px', overflow: 'auto'}}>
                                    {JSON.stringify(validationErrors, null, 2)}
                                </pre>
                            </div>
                            <div className="col-md-6">
                                <h6>Lecture Stats:</h6>
                                <ul className="small">
                                    <li>Total Lectures: {lectures.length}</li>
                                    <li>Valid Lectures: {lectures.filter(l => isLectureValid(l.id)).length}</li>
                                    <li>Invalid Lectures: {lectures.filter(l => !isLectureValid(l.id)).length}</li>
                                    <li>Total Validation Errors: {validationErrors && typeof validationErrors === 'object' ? Object.keys(validationErrors).reduce((sum, lectureId) => {
                                        const errors = validationErrors[lectureId];
                                        return sum + (Array.isArray(errors) ? errors.length : 0);
                                    }, 0) : 0}</li>
                                </ul>
                                <h6>Error Breakdown by Type:</h6>
                                <div style={{maxHeight: '100px', overflow: 'auto'}} className="mb-2">
                                    {(() => {
                                        try {
                                            const errorStats = {};
                                            
                                            // Safely iterate through validation errors
                                            if (validationErrors && typeof validationErrors === 'object') {
                                                Object.values(validationErrors).forEach(errors => {
                                                    // Check if errors is actually an array
                                                    if (Array.isArray(errors)) {
                                                        errors.forEach(error => {
                                                            if (error && error.field) {
                                                                if (!errorStats[error.field]) {
                                                                    errorStats[error.field] = { count: 0, messages: new Set() };
                                                                }
                                                                errorStats[error.field].count++;
                                                                errorStats[error.field].messages.add(error.message || 'Unknown error');
                                                            }
                                                        });
                                                    }
                                                });
                                            }
                                            
                                            return Object.entries(errorStats)
                                                .sort((a, b) => b[1].count - a[1].count)
                                                .map(([field, stats]) => (
                                                    <div key={field} className="small mb-1">
                                                        <strong>{field}:</strong> {stats.count} lectures ({Array.from(stats.messages).join(', ')})
                                                    </div>
                                                ));
                                        } catch (error) {
                                            return (
                                                <div className="small text-danger">
                                                    Error processing validation data: {error.message}
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                                <h6>Error Breakdown by Lecture:</h6>
                                <div style={{maxHeight: '100px', overflow: 'auto'}}>
                                    {validationErrors && typeof validationErrors === 'object' ? Object.entries(validationErrors).map(([lectureId, errors]) => {
                                        const lecture = lectures.find(l => l.id === lectureId);
                                        if (!Array.isArray(errors)) return null;
                                        
                                        return (
                                            <div key={lectureId} className="small mb-1">
                                                <strong>{lecture?.title?.[0]?.text || 'Unknown'} ({lectureId.slice(-8)}):</strong> {errors.length} errors
                                                <div className="text-muted ms-2">
                                                    {errors.map(e => e.field).filter(Boolean).join(', ')}
                                                </div>
                                            </div>
                                        );
                                    }).filter(Boolean) : (
                                        <div className="small text-muted">No validation errors found</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            )}

            {/* Enhanced Lectures Table */}
            <MDBCard>
                <MDBCardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <MDBCardTitle>
                            <h2 className="mb-0">{t("Lectures")} ({lectures.length})</h2>
                        </MDBCardTitle>
                        
                    </div>
                    
                    <MDBTable>
                        <MDBTableHead>
                            <tr>
                                <th>{t("Event")}</th>
                                <th>{t("Title")}</th>
                                <th>{t("Type")}</th>
                                <th>{t("Speakers")}</th>
                                <th>{t("Date, Time & Room")}</th>
                                <th>{t("Status")}</th>
                                <th width="250">{t("Documents")}</th>
                                <th width="150">{t("Recording")}</th>
                                <th>{t("Actions")}</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {lectures.length > 0 ? (
                                <>
                                {/* Current event lectures */}
                                {currentEventLectures.map((lecture) => {
                                    const lectureErrors = getLectureValidationErrors(lecture.id);
                                    const isValid = isLectureValid(lecture.id);

                                    return (
                                        <tr key={lecture.id}>
                                            <td>
                                                <small className="text-muted">
                                                    {lecture.event?.name && (lecture.event?.name.find(n => n.language === i18n.language)?.text || lecture.event?.name?.[0]?.text)}
                                                </small>
                                            </td>
                                            <td>
                                                <strong>{lecture.title?.find(t => t.language === i18n.language)?.text || lecture.title?.[0]?.text}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${
                                                    lecture.type === 'vip' ? 'bg-primary' :
                                                    lecture.type === 'technical' ? 'bg-info' :
                                                    lecture.type === 'educational' ? 'bg-success' :
                                                    'bg-secondary'
                                                }`}>
                                                    {t(lecture.type)}
                                                </span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    // Speakers are already unified (employee-based and user-based combined)
                                                    const allSpeakers = (lecture.speakers?.items || []).map(s => ({
                                                        image: s?.employee?.image || s?.user?.image,
                                                        firstName: s?.employee?.user?.firstName || s?.user?.firstName,
                                                        lastName: s?.employee?.user?.lastName || s?.user?.lastName
                                                    }));
                                                    const totalCount = allSpeakers.length;
                                                    const displayCount = totalCount > 4 ? 4 : totalCount;
                                                    const remainingCount = totalCount - displayCount;

                                                    return (
                                                        <div className="d-flex align-items-center">
                                                            {allSpeakers.slice(0, displayCount).map((speaker, index) => (
                                                                <div key={index} className="me-1">
                                                                    {speaker.image?.fileName ? (
                                                                        <img
                                                                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.image.fileName}`}
                                                                            className="rounded-circle border"
                                                                            style={{
                                                                                height: "30px",
                                                                                width: "30px",
                                                                                objectFit: "cover",
                                                                            }}
                                                                            alt={speaker.image.alt}
                                                                            title={`${speaker.firstName} ${speaker.lastName}`}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                height: "30px",
                                                                                width: "30px",
                                                                            }}
                                                                            title={`${speaker.firstName} ${speaker.lastName}`}
                                                                        >
                                                                            <MDBIcon fas icon="user" size="sm" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {remainingCount > 0 && (
                                                                <small className="text-muted">+{remainingCount}</small>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                {lecture.startTime ? (
                                                    <div>
                                                        <div><strong>{isoToGermanDate(lecture.startTime)}</strong></div>
                                                        <small className="text-muted">
                                                            {lecture.startTime.substring(11, 16)} - {lecture.endTime?.substring(11, 16)}
                                                        </small>
                                                        {lecture.room && (
                                                            <div><small className="text-muted">{t("Room")}: {lecture.room}</small></div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">{t("Not scheduled yet")}</span>
                                                )}
                                            </td>
                                            <td>
                                                {(() => {
                                                    // Only show validation results for lectures from the specific event
                                                    const targetEventId = "cb7a916e-ed71-483a-8177-22532ab0b237";
                                                    const shouldShowValidation = lecture.event?.id === targetEventId;
                                                    
                                                    if (!shouldShowValidation) {
                                                        return null;
                                                    }
                                                    
                                                    return isValid ? (
                                                        <div className="text-success">
                                                            <MDBIcon fas icon="check-circle" className="me-1" />
                                                            <small>{t("Complete")}</small>
                                                        </div>
                                                    ) : (
                                                        <div className="text-warning">
                                                            <div className="mt-1">
                                                                {lectureErrors.map((error, index) => {
                                                                    // Translate field names to user-friendly labels
                                                                    const getFieldLabel = (field) => {
                                                                        const fieldMap = {
                                                                            'generalConditions': t('General Conditions'),
                                                                            'speakers': t('Speakers'),
                                                                            'topics.0': t('Topics'),
                                                                            'topics': t('Topics'),
                                                                            'subjects': t('Subjects'),
                                                                            'handout': t('Handout'),
                                                                            'title': t('Title'),
                                                                            'description': t('Description'),
                                                                            'content': t('Content'),
                                                                            'type': t('Type'),
                                                                            'duration': t('Duration'),
                                                                            'targetAudience': t('Target Audience'),
                                                                            'prerequisites': t('Prerequisites'),
                                                                            'learningGoals': t('Learning Goals'),
                                                                            'abstract': t('Abstract'),
                                                                            'biography': t('Biography'),
                                                                            'conditions': t('Conditions'),
                                                                            "image": t("Image")
                                                                        };
                                                                        return fieldMap[field] || field;
                                                                    };

                                                                    const getMessageText = (message, field) => {
                                                                        // Handle legacy/technical error messages
                                                                        if (message === 'Required') {
                                                                            return t('Missing');
                                                                        }
                                                                        if (message === 'Expected array, received object') {
                                                                            if (field === 'speakers') {
                                                                                return t('Data structure issue (speakers.items)');
                                                                            }
                                                                            return t('Wrong data type (expected list)');
                                                                        }
                                                                        if (message === 'Expected string, received object') {
                                                                            if (field === 'topics.0' || field.startsWith('topics.')) {
                                                                                return t('Data structure issue (expected text)');
                                                                            }
                                                                            return t('Wrong data type (expected text)');
                                                                        }
                                                                        
                                                                        // Handle new validation messages
                                                                        const messageMap = {
                                                                            // Image messages
                                                                            'Please upload an image for the lecture': t('Please upload an image for the lecture'),
                                                                            'Image required': t('Image required'),
                                                                            
                                                                            // Speaker messages
                                                                            'At least one main speaker must be selected': t('At least one main speaker must be selected'),
                                                                            'All main speakers must have a valid employee ID': t('All main speakers must have a valid employee ID'),
                                                                            
                                                                            // Title messages
                                                                            'Title in German and English required': t('Title in German and English required'),
                                                                            'Lecture type required': t('Lecture type required'),
                                                                            'Valid lecture type required': t('Valid lecture type required'),
                                                                            
                                                                            // Content messages
                                                                            'Description in German and English required': t('Description in German and English required'),
                                                                            
                                                                            // Subjects messages
                                                                            'At least 3 subpoints required': t('At least 3 subpoints required'),
                                                                            'Maximum 5 subpoints allowed': t('Maximum 5 subpoints allowed'),
                                                                            'At least 3 subpoints must be completely filled in (both languages)': t('At least 3 subpoints must be completely filled in (both languages)'),
                                                                            'When a subpoint is filled in, both languages (German and English) must be filled in': t('When a subpoint is filled in, both languages (German and English) must be filled in'),
                                                                            
                                                                            // Outline messages
                                                                            'All outline point titles must be filled in': t('All outline point titles must be filled in'),
                                                                            
                                                                            // Conditions messages
                                                                            'Condition must be accepted': t('Condition must be accepted'),
                                                                            'All required general conditions must be accepted': t('All required general conditions must be accepted'),
                                                                            'Invalid general condition': t('Invalid general condition'),
                                                                            'General conditions': t('General conditions'),
                                                                            'All required conditions must be accepted': t('All required conditions must be accepted'),
                                                                            'Invalid condition for this lecture type': t('Invalid condition for this lecture type'),
                                                                            'Conditions': t('Conditions'),
                                                                            
                                                                            // Topics messages
                                                                            'Event location required': t('Event location required'),
                                                                            'Target audience required': t('Target audience required'),
                                                                            'At least 1 participant': t('At least 1 participant'),
                                                                            'Prerequisites required': t('Prerequisites required'),
                                                                            'Valid meeting link required': t('Valid meeting link required'),
                                                                            'Recording permission required': t('Recording permission required'),
                                                                            
                                                                            // Handout messages
                                                                            'At least one handout (German or English) must be uploaded': t('At least one handout (German or English) must be uploaded'),
                                                                            
                                                                            // Language and text validation
                                                                            'Language is required': t('Language is required'),
                                                                            'Text is required': t('Text is required')
                                                                        };
                                                                        
                                                                        // Handle dynamic messages with variables
                                                                        if (message.startsWith('At least ') && message.includes('speaker(s) required')) {
                                                                            const match = message.match(/At least (\d+) speaker\(s\) required/);
                                                                            if (match) {
                                                                                return t('At least {{count}} speaker(s) required', { count: match[1] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('At least ') && message.includes('topic area')) {
                                                                            const match = message.match(/At least (\d+) topic area(s?) required/);
                                                                            if (match) {
                                                                                return t('At least {{count}} topic area{{plural}} required', { count: match[1], plural: match[2] || '' });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('Maximum ') && message.includes('topic areas')) {
                                                                            const match = message.match(/Maximum (\d+) topic areas allowed/);
                                                                            if (match) {
                                                                                return t('Maximum {{count}} topic areas allowed', { count: match[1] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('At least ') && message.includes('outline points')) {
                                                                            const match = message.match(/At least (\d+) outline points required/);
                                                                            if (match) {
                                                                                return t('At least {{count}} outline points required', { count: match[1] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('Maximum ') && message.includes('outline points')) {
                                                                            const match = message.match(/Maximum (\d+) outline points allowed/);
                                                                            if (match) {
                                                                                return t('Maximum {{count}} outline points allowed', { count: match[1] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('Title must be at least') || message.startsWith('Title may be at most')) {
                                                                            const match = message.match(/(Title must be at least|Title may be at most) (\d+) characters/);
                                                                            if (match) {
                                                                                const type = match[1] === 'Title must be at least' ? 'minimum' : 'maximum';
                                                                                return t('Title {{type}} {{count}} characters', { type: t(type), count: match[2] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.startsWith('Text must be at least') || message.startsWith('Text may be at most')) {
                                                                            const match = message.match(/(Text must be at least|Text may be at most) (\d+) characters/);
                                                                            if (match) {
                                                                                const type = match[1] === 'Text must be at least' ? 'minimum' : 'maximum';
                                                                                return t('Text {{type}} {{count}} characters', { type: t(type), count: match[2] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.includes('must be between') && message.includes('characters long')) {
                                                                            const match = message.match(/(Title|Description) in (.+) must be between (\d+) and (\d+) characters long/);
                                                                            if (match) {
                                                                                return t('{{field}} in {{languages}} must be between {{min}} and {{max}} characters long', {
                                                                                    field: t(match[1]),
                                                                                    languages: match[2],
                                                                                    min: match[3],
                                                                                    max: match[4]
                                                                                });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.includes('may not be empty')) {
                                                                            const match = message.match(/(Title|Description) in (.+) may not be empty/);
                                                                            if (match) {
                                                                                return t('{{field}} in {{languages}} may not be empty', {
                                                                                    field: t(match[1]),
                                                                                    languages: match[2]
                                                                                });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.includes('required') && (message.includes('Title in') || message.includes('Description in'))) {
                                                                            const match = message.match(/(Title|Description) in (.+) required/);
                                                                            if (match) {
                                                                                return t('{{field}} in {{languages}} required', {
                                                                                    field: t(match[1]),
                                                                                    languages: match[2]
                                                                                });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.includes('minutes')) {
                                                                            const match = message.match(/(At least|Maximum) (\d+) minutes/);
                                                                            if (match) {
                                                                                const type = match[1] === 'At least' ? 'minimum' : 'maximum';
                                                                                return t('{{type}} {{count}} minutes', { type: t(type), count: match[2] });
                                                                            }
                                                                        }
                                                                        
                                                                        if (message.includes('participants')) {
                                                                            const match = message.match(/(At least|Maximum) (\d+) participants/);
                                                                            if (match) {
                                                                                const type = match[1] === 'At least' ? 'minimum' : 'maximum';
                                                                                return t('{{type}} {{count}} participants', { type: t(type), count: match[2] });
                                                                            }
                                                                        }
                                                                        
                                                                        // Check if we have a direct translation
                                                                        if (messageMap[message]) {
                                                                            return messageMap[message];
                                                                        }
                                                                        
                                                                        // Fallback: return the original message
                                                                        return message;
                                                                    };

                                                                    return (
                                                                        <div key={index} className="small text-danger mb-1 d-flex align-items-center">
                                                                            <MDBIcon fas icon="times-circle" size="sm" className="me-1" />
                                                                            <strong>{getFieldLabel(error.field)}:</strong>
                                                                            <span className="ms-1">{getMessageText(error.message, error.field)}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                {/* Admin-set status display */}
                                                {lecture.status && lecture.status !== "created" && (
                                                    <div className={`mt-2 ${
                                                        lecture.status === "checked" ? "text-success" :
                                                        lecture.status === "not_approved" ? "text-danger" :
                                                        lecture.status === "in_planning" ? "text-warning" :
                                                        lecture.status === "committed" ? "text-primary" :
                                                        "text-muted"
                                                    }`}>
                                                        <small>
                                                            <MDBIcon fas icon={
                                                                lecture.status === "checked" ? "check-circle" :
                                                                lecture.status === "not_approved" ? "times-circle" :
                                                                lecture.status === "in_planning" ? "clock" :
                                                                lecture.status === "committed" ? "calendar-check" :
                                                                "info-circle"
                                                            } className="me-1" />
                                                            {lecture.status === "checked" ? t("Approved") :
                                                             lecture.status === "not_approved" ? t("Not Approved") :
                                                             lecture.status === "in_planning" ? t("In Planning") :
                                                             lecture.status === "committed" ? t("Committed") :
                                                             t(lecture.status)}
                                                        </small>
                                                    </div>
                                                )}
                                                {lecture.adminComment && lecture.adminComment.length > 2 && (
                                                    <div className="mt-2 p-2 bg-light border rounded">
                                                        <small className="text-info d-block mb-1">
                                                            <MDBIcon fas icon="comment" className="me-1" />
                                                            {t("Admin Comment")}:
                                                        </small>
                                                        <small className="text-dark">{lecture.adminComment}</small>
                                                    </div>
                                                )}
                                                
                                                {/* Dev Mode: Raw Validation Data */}
                                                {isDevMode && (
                                                    <div className="mt-3 p-2 bg-light border rounded">
                                                        <small className="text-muted d-block mb-2">
                                                            <MDBIcon fas icon="code" className="me-1" />
                                                            Dev Mode: Raw Validation Data
                                                        </small>
                                                        <div className="small">
                                                            <div><strong>isValid:</strong> {isValid.toString()}</div>
                                                            <div><strong>Errors ({lectureErrors.length}):</strong></div>
                                                            <pre className="small mb-0" style={{fontSize: '10px', maxHeight: '120px', overflow: 'auto'}}>
                                                                {JSON.stringify(lectureErrors, null, 2)}
                                                            </pre>
                                                            <div className="mt-1"><strong>Relevant Validation Data (Transformed):</strong></div>
                                                            <pre className="small mb-0" style={{fontSize: '10px', maxHeight: '120px', overflow: 'auto'}}>
                                                                {JSON.stringify(lecture)}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <small>
                                                            {t("Handout")}:
                                                            <MDBTooltip tag="span" wrapperClass="ms-1" title={t("The handout will be made available to event participants.")}>
                                                                <MDBIcon fas icon="info-circle" size="sm" className="text-muted" />
                                                            </MDBTooltip>
                                                        </small>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`me-1 ${lecture.pdfDe?.fileName ? 'text-success' : 'text-danger'}`}>
                                                                DE <MDBIcon fas icon={lecture.pdfDe?.fileName ? "check" : "times"} size="sm" />
                                                            </span>
                                                            <span className={`me-1 ${lecture.pdfEn?.fileName ? 'text-success' : 'text-danger'}`}>
                                                                EN <MDBIcon fas icon={lecture.pdfEn?.fileName ? "check" : "times"} size="sm" />
                                                            </span>
                                                            {isCompanyAdmin && (
                                                                <MDBBtn
                                                                    size="sm"
                                                                    color='link'
                                                                    className="p-1"
                                                                    disabled={!canUploadDocumentsForLecture(lecture)}
                                                                    onClick={() => handleOpenDocumentUploadModal(lecture.id)}
                                                                >
                                                                    <MDBIcon fas icon="upload" size="sm" />
                                                                </MDBBtn>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <small>
                                                            {t("Presentation")}:
                                                            <MDBTooltip tag="span" wrapperClass="ms-1" title={t("Upload your presentation here, which will be shown during your talk. You don't need to bring it yourself.")}>
                                                                <MDBIcon fas icon="info-circle" size="sm" className="text-muted" />
                                                            </MDBTooltip>
                                                        </small>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`me-1 ${lecture.presentation?.fileName ? 'text-success' : 'text-danger'}`}>
                                                                <MDBIcon fas icon={lecture.presentation?.fileName ? "check" : "times"} size="sm" />
                                                            </span>
                                                            {isCompanyAdmin && (
                                                                <MDBBtn
                                                                    size="sm"
                                                                    color='link'
                                                                    className="p-1"
                                                                    disabled={!canUploadPresentationForLecture(lecture)}
                                                                    onClick={() => handleOpenPresentationUploadModal(lecture.id)}
                                                                >
                                                                    <MDBIcon fas icon="upload" size="sm" />
                                                                </MDBBtn>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {lecture.videoS3Key?.s3Path && (
                                                    <div className="d-flex flex-column gap-1">
                                                        <MDBBtn 
                                                            color='link' 
                                                            size="sm"
                                                            className="p-1 text-start" 
                                                            onClick={() => handleOpenVideoModal(lecture.id)}
                                                        >
                                                            <MDBIcon fas icon="play" className="me-1" />
                                                            {t("View")}
                                                        </MDBBtn>
                                                        <MDBBtn 
                                                            color='link' 
                                                            size="sm"
                                                            className="p-1 text-start" 
                                                            onClick={() => handleOpenStatisticsModal(lecture.id)}
                                                        >
                                                            <MDBIcon fas icon="chart-bar" className="me-1" />
                                                            {t("Stats")}
                                                        </MDBBtn>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    {adminActions ? (
                                                        <Link to={`/admin/lecture/${companyid}/${lecture.id}`}>
                                                            <MDBBtn color='primary' size="sm" className="w-100">
                                                                <MDBIcon fas icon="edit" className="me-1" />
                                                                {t("Edit")}
                                                            </MDBBtn>
                                                        </Link>
                                                    ) : canEditLectureDevMode(lecture) && (
                                                        <>
                                                            <MDBBtn 
                                                                color='primary' 
                                                                size="sm"
                                                                className="w-100"
                                                                onClick={() => handleEditLecture(lecture)}
                                                            >
                                                                <MDBIcon fas icon="edit" className="me-1" />
                                                                {t("Edit")}
                                                                {isDevMode && !canEditLecture(lecture) && (
                                                                    <MDBIcon fas icon="tools" className="ms-1 text-warning" title="DevMode: Normally disabled" />
                                                                )}
                                                            </MDBBtn>
                                                           
                                                        </>
                                                    )}
                                                    
                                                    {canDeleteLectureDevMode(lecture) && (
                                                        <MDBBtn 
                                                            color='danger' 
                                                            outline
                                                            size="sm"
                                                            className="w-100"
                                                            onClick={() => {
                                                                setSelectedLectureRemove(lecture.id); 
                                                                setDeleteLectureModal(true);
                                                            }}
                                                        >
                                                            <MDBIcon fas icon="trash" className="me-1" />
                                                            {t("Delete")}
                                                            {isDevMode && !canDeleteLecture(lecture) && (
                                                                <MDBIcon fas icon="tools" className="ms-1 text-warning" title="DevMode: Normally disabled" />
                                                            )}
                                                        </MDBBtn>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* Separator for old event lectures */}
                                {oldEventLectures.length > 0 && (
                                    <tr className="bg-light">
                                        <td colSpan="9" className="py-3">
                                            <div className="d-flex align-items-center">
                                                <hr className="flex-grow-1 me-3" />
                                                <span className="text-muted fw-bold">
                                                    <MDBIcon fas icon="history" className="me-2" />
                                                    {t("Old event lectures")}
                                                </span>
                                                <hr className="flex-grow-1 ms-3" />
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {/* Old event lectures */}
                                {oldEventLectures.map((lecture) => {
                                    return (
                                        <tr key={lecture.id} className="table-secondary">
                                            <td>
                                                <small className="text-muted">
                                                    {lecture.event?.name && (lecture.event?.name.find(n => n.language === i18n.language)?.text || lecture.event?.name?.[0]?.text)}
                                                </small>
                                            </td>
                                            <td>
                                                <strong>{lecture.title?.find(t => t.language === i18n.language)?.text || lecture.title?.[0]?.text}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${
                                                    lecture.type === 'vip' ? 'bg-primary' :
                                                    lecture.type === 'technical' ? 'bg-info' :
                                                    lecture.type === 'educational' ? 'bg-success' :
                                                    'bg-secondary'
                                                }`}>
                                                    {t(lecture.type)}
                                                </span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const allSpeakers = (lecture.speakers?.items || []).map(s => ({
                                                        image: s?.employee?.image || s?.user?.image,
                                                        firstName: s?.employee?.user?.firstName || s?.user?.firstName,
                                                        lastName: s?.employee?.user?.lastName || s?.user?.lastName
                                                    }));
                                                    const totalCount = allSpeakers.length;
                                                    const displayCount = totalCount > 4 ? 4 : totalCount;
                                                    const remainingCount = totalCount - displayCount;

                                                    return (
                                                        <div className="d-flex align-items-center">
                                                            {allSpeakers.slice(0, displayCount).map((speaker, index) => (
                                                                <div key={index} className="me-1">
                                                                    {speaker.image?.fileName ? (
                                                                        <img
                                                                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.image.fileName}`}
                                                                            className="rounded-circle border"
                                                                            style={{
                                                                                height: "30px",
                                                                                width: "30px",
                                                                                objectFit: "cover",
                                                                            }}
                                                                            alt={speaker.image.alt}
                                                                            title={`${speaker.firstName} ${speaker.lastName}`}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                height: "30px",
                                                                                width: "30px",
                                                                            }}
                                                                            title={`${speaker.firstName} ${speaker.lastName}`}
                                                                        >
                                                                            <MDBIcon fas icon="user" size="sm" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {remainingCount > 0 && (
                                                                <small className="text-muted">+{remainingCount}</small>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                {lecture.startTime ? (
                                                    <div>
                                                        <div><strong>{isoToGermanDate(lecture.startTime)}</strong></div>
                                                        <small className="text-muted">
                                                            {lecture.startTime.substring(11, 16)} - {lecture.endTime?.substring(11, 16)}
                                                        </small>
                                                        {lecture.room && (
                                                            <div><small className="text-muted">{t("Room")}: {lecture.room}</small></div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">{t("Not scheduled yet")}</span>
                                                )}
                                            </td>
                                            <td>
                                                {/* Admin-set status display for old lectures */}
                                                {lecture.status && lecture.status !== "created" && (
                                                    <div className={`${
                                                        lecture.status === "checked" ? "text-success" :
                                                        lecture.status === "not_approved" ? "text-danger" :
                                                        lecture.status === "in_planning" ? "text-warning" :
                                                        lecture.status === "committed" ? "text-primary" :
                                                        "text-muted"
                                                    }`}>
                                                        <small>
                                                            <MDBIcon fas icon={
                                                                lecture.status === "checked" ? "check-circle" :
                                                                lecture.status === "not_approved" ? "times-circle" :
                                                                lecture.status === "in_planning" ? "clock" :
                                                                lecture.status === "committed" ? "calendar-check" :
                                                                "info-circle"
                                                            } className="me-1" />
                                                            {lecture.status === "checked" ? t("Approved") :
                                                             lecture.status === "not_approved" ? t("Not Approved") :
                                                             lecture.status === "in_planning" ? t("In Planning") :
                                                             lecture.status === "committed" ? t("Committed") :
                                                             t(lecture.status)}
                                                        </small>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <small>
                                                            {t("Handout")}:
                                                            <MDBTooltip tag="span" wrapperClass="ms-1" title={t("The handout will be made available to event participants.")}>
                                                                <MDBIcon fas icon="info-circle" size="sm" className="text-muted" />
                                                            </MDBTooltip>
                                                        </small>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`me-1 ${lecture.pdfDe?.fileName ? 'text-success' : 'text-danger'}`}>
                                                                DE <MDBIcon fas icon={lecture.pdfDe?.fileName ? "check" : "times"} size="sm" />
                                                            </span>
                                                            <span className={`me-1 ${lecture.pdfEn?.fileName ? 'text-success' : 'text-danger'}`}>
                                                                EN <MDBIcon fas icon={lecture.pdfEn?.fileName ? "check" : "times"} size="sm" />
                                                            </span>
                                                            {isCompanyAdmin && canUploadDocumentsForLecture(lecture) && (
                                                                <MDBBtn
                                                                    size="sm"
                                                                    color='link'
                                                                    className="p-1"
                                                                    onClick={() => handleOpenDocumentUploadModal(lecture.id)}
                                                                >
                                                                    <MDBIcon fas icon="upload" size="sm" />
                                                                </MDBBtn>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <small>
                                                            {t("Presentation")}:
                                                            <MDBTooltip tag="span" wrapperClass="ms-1" title={t("Upload your presentation here, which will be shown during your talk. You don't need to bring it yourself.")}>
                                                                <MDBIcon fas icon="info-circle" size="sm" className="text-muted" />
                                                            </MDBTooltip>
                                                        </small>
                                                        <span className={`${lecture.presentation?.fileName ? 'text-success' : 'text-danger'}`}>
                                                            <MDBIcon fas icon={lecture.presentation?.fileName ? "check" : "times"} size="sm" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {lecture.videoS3Key?.s3Path && (
                                                    <div className="d-flex flex-column gap-1">
                                                        <MDBBtn
                                                            color='link'
                                                            size="sm"
                                                            className="p-1 text-start"
                                                            onClick={() => handleOpenVideoModal(lecture.id)}
                                                        >
                                                            <MDBIcon fas icon="play" className="me-1" />
                                                            {t("View")}
                                                        </MDBBtn>
                                                        <MDBBtn
                                                            color='link'
                                                            size="sm"
                                                            className="p-1 text-start"
                                                            onClick={() => handleOpenStatisticsModal(lecture.id)}
                                                        >
                                                            <MDBIcon fas icon="chart-bar" className="me-1" />
                                                            {t("Stats")}
                                                        </MDBBtn>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {/* Old lectures: only view, no edit/delete */}
                                                <small className="text-muted">
                                                    <MDBIcon fas icon="archive" className="me-1" />
                                                    {t("Archived")}
                                                </small>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <MDBIcon fas icon="chalkboard-teacher" size="3x" className="text-muted mb-3" />
                                        <h5 className="text-muted">{t("No lectures found")}</h5>
                                        <p className="text-muted">{t("Register your first lecture for an upcoming event.")}</p>
                                    </td>
                                </tr>
                            )}
                        </MDBTableBody>
                    </MDBTable>
                </MDBCardBody>
            </MDBCard>

            {/* Create Lecture Modal */}
            <MDBModal open={showCreateModal} onClose={() => {}} staticBackdrop>
                <MDBModalDialog size="fullscreen">
                    <MDBModalContent>
                        <MDBModalBody className="p-0">
                            <LectureRegistration
                                companyId={companyid}
                                eventId={selectedEventForLecture?.id}
                                lectureId="new"
                                eventConfig={lounges26LectureConfig}
                                onSaveLecture={handleSaveLecture}
                                onCloseRequest={handleCloseModals}
                                isModal={true}
                            />
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Edit Lecture Modal */}
            <MDBModal open={showEditModal} onClose={() => {}} staticBackdrop>
                <MDBModalDialog size="fullscreen">
                    <MDBModalContent>
                        <MDBModalBody className="p-0">
                            <LectureRegistration
                                companyId={companyid}
                                eventId={selectedEventForLecture?.id}
                                lectureId={selectedLectureForEdit}
                                eventConfig={lounges26LectureConfig}
                                onSaveLecture={handleSaveLecture}
                                onCloseRequest={handleCloseModals}
                                isModal={true}
                            />
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Delete Lecture Modal */}
            <MDBModal open={deleteLectureModal} onClose={() => setDeleteLectureModal(false)}>
                <MDBModalDialog centered size="sm">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("Remove lecture")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setDeleteLectureModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody className="text-center">
                            <MDBIcon fas icon="exclamation-triangle" size="3x" className="text-warning mb-3" />
                            <p>{t("Are you sure you want to remove this lecture?")}</p>
                            <p className="fw-bold">
                                {selectedLectureRemove && (
                                    lectures.find(l => l.id === selectedLectureRemove)?.title?.find(t => t.language === i18n.language)?.text ?? 
                                    lectures.find(l => l.id === selectedLectureRemove)?.title?.[0]?.text
                                )}
                            </p>
                            <div className="d-flex gap-2 justify-content-center">
                                <MDBBtn color='secondary' onClick={() => setDeleteLectureModal(false)}>
                                    {t("Cancel")}
                                </MDBBtn>
                                <MDBBtn 
                                    color='danger' 
                                    onClick={() => handleDeleteLecture(selectedLectureRemove)}
                                    disabled={isDeletingLecture}
                                >
                                    {isDeletingLecture ? (
                                        <><MDBSpinner size='sm' className='me-2' />{t("Deleting...")}</>
                                    ) : (
                                        <><MDBIcon fas icon="trash" className="me-1" />{t("Remove")}</>
                                    )}
                                </MDBBtn>
                            </div>
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Video Modal */}
            <MDBModal open={videoModal} onClose={() => setVideoModal(false)}>
                <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("View recording")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => {
                                setSelectedLectureVideo(null);
                                setSelectedVideoUrl(null);
                                setVideoModal(false);
                            }}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody className="text-center">
                            <h6>
                                {selectedLectureVideo && (
                                    lectures.find(l => l.id === selectedLectureVideo)?.title?.find(t => t.language === i18n.language)?.text ?? 
                                    lectures.find(l => l.id === selectedLectureVideo)?.title?.[0]?.text
                                )}
                            </h6>
                            {selectedVideoUrl ? (
                                <ReactPlayer
                                    url={selectedVideoUrl}
                                    controls
                                    width="100%"
                                    height="auto"
                                    config={{
                                        file: {
                                            attributes: {
                                                controlsList: "nodownload",
                                                onContextMenu: e => e.preventDefault()
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                                    <MDBSpinner className="me-2" />
                                    <span>{t("Loading video...")}</span>
                                </div>
                            )}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Statistics Modal */}
            <MDBModal open={statisticsModal} onClose={() => setStatisticsModal(false)}>
                <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("View statistics")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setStatisticsModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            <MDBTable>
                                <MDBTableHead>
                                    <tr>
                                        <th>{t("User")}</th>
                                        <th>{t("Watched Length")}</th>
                                    </tr>
                                </MDBTableHead>
                                <MDBTableBody>
                                    {lectures.find(l => l.id === selectedLectureVideo)?.videoProgress?.items?.map((videoProgress) => (
                                        <tr key={videoProgress.id}>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    {videoProgress.user?.image?.fileName ? (
                                                        <img
                                                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${videoProgress.user.image.fileName}`}
                                                            className="rounded-circle border"
                                                            style={{ height: "40px", width: "40px", objectFit: "cover" }}
                                                            alt={`${videoProgress.user.firstName} ${videoProgress.user.lastName}`}
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                            style={{ height: "40px", width: "40px" }}
                                                        >
                                                            <MDBIcon fas icon="user" />
                                                        </div>
                                                    )}
                                                    <div className='ms-3'>
                                                        <p className='fw-bold mb-1'>
                                                            {[videoProgress.user?.title, videoProgress.user?.firstName, videoProgress.user?.lastName]
                                                                .filter(Boolean).join(' ')}
                                                        </p>
                                                        <p className='text-muted mb-0 small'>{videoProgress.user?.contact?.address?.company || ""}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <strong>{Math.floor(videoProgress.totalWatchedInSeconds / 60)}m {videoProgress.totalWatchedInSeconds % 60}s</strong>
                                            </td>
                                        </tr>
                                    ))}
                                </MDBTableBody>
                            </MDBTable>
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Document Upload Modal */}
            <MDBModal open={documentUploadModal} onClose={() => setDocumentUploadModal(false)}>
                <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("Upload Lecture Documents")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setDocumentUploadModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {selectedLectureForDocuments && (() => {
                                const lecture = lectures.find(l => l.id === selectedLectureForDocuments);
                                return (
                                    <>
                                        <h5>{lecture?.title?.find(t => t.language === i18n.language)?.text ?? lecture?.title?.[0]?.text}</h5>
                                        
                                        <p className="text-muted mb-4">
                                            {t("Upload documents for participants. Deadline")}{": "}
                                            <strong>{isoToGermanDate(lecture?.event?.lectureDocumentsEndDate)}</strong>
                                        </p>
                                        
                                        {/* German Document Upload */}
                                        <div className="mb-4">
                                            <h6 className="d-flex align-items-center">
                                                <ReactCountryFlag countryCode="de" svg style={{ width: "20px", height: "20px" }} className="me-2" />
                                                {t("German Document")}
                                            </h6>
                                            <div className="d-flex align-items-center">
                                                {lecture?.pdfDe?.fileName ? (
                                                    <div className="d-flex flex-column w-100">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <MDBBtn 
                                                                color="link" 
                                                                onClick={() => handleDownloadFile(lecture.pdfDe.fileName, lecture.pdfDe.s3Path)} 
                                                                size="sm"
                                                            >
                                                                <MDBIcon fas icon="download" className="me-1" />
                                                                {lecture.pdfDe.fileName}
                                                            </MDBBtn>
                                                            <MDBBtn 
                                                                color="link" 
                                                                className="text-danger" 
                                                                size="sm" 
                                                                onClick={() => handleRemoveFile(lecture.pdfDe.fileName, lecture.pdfDe.s3Path, 'De', selectedLectureForDocuments)}
                                                            >
                                                                <MDBIcon fas icon="trash" />
                                                            </MDBBtn>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MDBFile 
                                                        id='fileDocDe' 
                                                        onChange={e => handleSetFile(e.target.files[0], 'De')}
                                                        className="w-100"
                                                    />
                                                )}
                                                {isUploadingLectureFiles.De && <MDBSpinner size='sm' className='ms-2' />}
                                            </div>
                                            {uploadingLecturesErrors.De && (
                                                <div className="text-danger small mt-2">{uploadingLecturesErrors.De}</div>
                                            )}
                                        </div>
                                        
                                        {/* English Document Upload */}
                                        <div>
                                            <h6 className="d-flex align-items-center">
                                                <ReactCountryFlag countryCode="gb" svg style={{ width: "20px", height: "20px" }} className="me-2" />
                                                {t("English Document")}
                                            </h6>
                                            <div className="d-flex align-items-center">
                                                {lecture?.pdfEn?.fileName ? (
                                                    <div className="d-flex flex-column w-100">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <MDBBtn 
                                                                color="link" 
                                                                onClick={() => handleDownloadFile(lecture.pdfEn.fileName, lecture.pdfEn.s3Path)} 
                                                                size="sm"
                                                            >
                                                                <MDBIcon fas icon="download" className="me-1" />
                                                                {lecture.pdfEn.fileName}
                                                            </MDBBtn>
                                                            <MDBBtn 
                                                                color="link" 
                                                                className="text-danger" 
                                                                size="sm" 
                                                                onClick={() => handleRemoveFile(lecture.pdfEn.fileName, lecture.pdfEn.s3Path, 'En', selectedLectureForDocuments)}
                                                            >
                                                                <MDBIcon fas icon="trash" />
                                                            </MDBBtn>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MDBFile 
                                                        id='fileDocEn' 
                                                        onChange={e => handleSetFile(e.target.files[0], 'En')}
                                                        className="w-100"
                                                    />
                                                )}
                                                {isUploadingLectureFiles.En && <MDBSpinner size='sm' className='ms-2' />}
                                            </div>
                                            {uploadingLecturesErrors.En && (
                                                <div className="text-danger small mt-2">{uploadingLecturesErrors.En}</div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            {/* Presentation Upload Modal */}
            <MDBModal open={presentationUploadModal} onClose={() => setPresentationUploadModal(false)}>
                <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("Upload Presentation")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setPresentationUploadModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {selectedLectureForDocuments && (() => {
                                const lecture = lectures.find(l => l.id === selectedLectureForDocuments);
                                return (
                                    <>
                                        <h5>{lecture?.title?.find(t => t.language === i18n.language)?.text ?? lecture?.title?.[0]?.text}</h5>
                                        
                                        <p className="text-muted mb-4">
                                            {t("Upload presentation file (PowerPoint preferred). Deadline")}{": "}
                                            <strong>{isoToGermanDate(lecture?.event?.lecturePresentationEndDate)}</strong>
                                        </p>
                                        
                                        <div className="mb-4">
                                            <h6>{t("Presentation File")}</h6>
                                            <div className="d-flex align-items-center">
                                                {lecture?.presentation?.fileName ? (
                                                    <div className="d-flex flex-column w-100">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <MDBBtn 
                                                                color="link" 
                                                                onClick={() => handleDownloadFile(lecture.presentation.fileName, lecture.presentation.s3Path)} 
                                                                size="sm"
                                                            >
                                                                <MDBIcon fas icon="download" className="me-1" />
                                                                {lecture.presentation.fileName}
                                                            </MDBBtn>
                                                            <MDBBtn 
                                                                color="link" 
                                                                className="text-danger" 
                                                                size="sm" 
                                                                onClick={() => handleRemoveFile(lecture.presentation.fileName, lecture.presentation.s3Path, 'Presentation', selectedLectureForDocuments)}
                                                            >
                                                                <MDBIcon fas icon="trash" />
                                                            </MDBBtn>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MDBFile 
                                                        id='filePresentation' 
                                                        onChange={e => handleSetFile(e.target.files[0], 'Presentation')}
                                                        className="w-100"
                                                    />
                                                )}
                                                {isUploadingLectureFiles.Presentation && <MDBSpinner size='sm' className='ms-2' />}
                                            </div>
                                            {uploadingLecturesErrors.Presentation && (
                                                <div className="text-danger small mt-2">{uploadingLecturesErrors.Presentation}</div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </>
    );
}